import type {
  DetectAllOptions,
  DetectOptions,
  DetectionReport,
  ExtensionDefinition,
  ExtensionResult,
  SignalDefinition,
  SignalResult,
} from './types';

/**
 * Detection has two wildly different cost classes, and conflating them is what made a
 * full scan take 12 seconds.
 *
 *  - A **resource probe** asks whether `chrome-extension://<id>/<path>` loads. It answers
 *    in ~0.2ms, hit or miss, because the answer is a local file lookup.
 *  - A **behavioural probe** presents bait and waits to see whether an extension reacts.
 *    There is no "no" event to wait for, so a negative costs the entire timeout — 1.5s
 *    for most, 4s for Skip Ads, 12s for Apollo (which takes 10-20s to inject).
 *
 * 1,834 of the 1,855 definitions are in the first class and finish together in about a
 * second. The remaining 21 set the wall clock of the whole report. So the tiers are split
 * here rather than in the UI: the fast tier is reported the moment it is done, and the
 * slow tier lands later for callers that want it.
 */
export const isBehavioural = (def: ExtensionDefinition): boolean =>
  def.signals.some((s) => s.kind === 'dom');

/** Split a registry into the tier that answers in ~1ms and the tier that waits seconds. */
export function partitionBySpeed(defs: ExtensionDefinition[]): {
  fast: ExtensionDefinition[];
  slow: ExtensionDefinition[];
} {
  const fast: ExtensionDefinition[] = [];
  const slow: ExtensionDefinition[] = [];
  for (const def of defs) (isBehavioural(def) ? slow : fast).push(def);
  return { fast, slow };
}

/** Resolved when a probe outruns an explicit cap. A symbol, so it can't collide with an outcome. */
const TRUNCATED: unique symbol = Symbol('truncated');

function withCap<T>(promise: Promise<T>, ms: number): Promise<T | typeof TRUNCATED> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => resolve(TRUNCATED), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}

const baseOf = (signal: SignalDefinition) => ({
  id: signal.id,
  kind: signal.kind,
  description: signal.description,
  weight: signal.weight,
  control: signal.control ?? false,
});

async function runSignal(
  signal: SignalDefinition,
  maxProbeMs: number | null,
): Promise<SignalResult> {
  const start = performance.now();
  const base = baseOf(signal);
  try {
    const outcome =
      maxProbeMs == null ? await signal.run() : await withCap(signal.run(), maxProbeMs);
    if (outcome === TRUNCATED) {
      // The probe is abandoned, not cancelled — it still cleans itself up on its own
      // timeout (MutationObservers disconnect, bait removes itself). We stop waiting for
      // an answer, and are explicit that we never got one.
      return {
        ...base,
        fired: false,
        truncated: true,
        detail: `probe cut short at ${maxProbeMs}ms — it wanted longer, so this is "unknown", not "absent"`,
        durationMs: performance.now() - start,
      };
    }
    return { ...base, ...outcome, durationMs: performance.now() - start };
  } catch (err) {
    // A throwing probe is a bug in the probe, not evidence about the extension.
    return {
      ...base,
      fired: false,
      detail: `probe threw: ${(err as Error).message}`,
      error: (err as Error).message,
      durationMs: performance.now() - start,
    };
  }
}

/** A signal that was never run, because no probe on this origin could have fired. */
const skippedSignal = (signal: SignalDefinition, why: string): SignalResult => ({
  ...baseOf(signal),
  fired: false,
  skipped: true,
  detail: why,
  durationMs: 0,
});

/** location is unavailable in non-browser contexts (e.g. unit tests). */
const currentOrigin = () => (typeof location !== 'undefined' ? location.origin : null);

export async function detectExtension(
  def: ExtensionDefinition,
  { maxProbeMs = null }: DetectOptions = {},
): Promise<ExtensionResult> {
  const start = performance.now();

  const originMismatch = !!def.requiredOrigin && currentOrigin() !== def.requiredOrigin;

  // An extension whose resources are scoped to one origin cannot be probed from anywhere
  // else — those requests are guaranteed to fail, and failing still costs a real
  // `chrome-extension://` round trip and a console error apiece. Across the registry that
  // is ~1,100 requests that can only produce the answer we already have. The verdict is
  // identical either way: `detected: false, originMismatch: true`.
  const signals = originMismatch
    ? def.signals.map((s) =>
        skippedSignal(
          s,
          `not probed — only reachable from ${def.requiredOrigin}, page is on ${currentOrigin()}`,
        ),
      )
    : await Promise.all(def.signals.map((s) => runSignal(s, maxProbeMs)));

  const scoring = signals.filter((s) => !s.control);
  const totalWeight = scoring.reduce((sum, s) => sum + s.weight, 0);
  const firedWeight = scoring.filter((s) => s.fired).reduce((sum, s) => sum + s.weight, 0);
  const detected = scoring.some((s) => s.fired);

  return {
    id: def.id,
    name: def.name,
    storeUrl: def.storeUrl,
    users: def.users,
    notes: def.notes,
    requiredOrigin: def.requiredOrigin,
    requiresRealPage: def.requiresRealPage,
    interactive: def.interactive,
    originMismatch,
    // Any positive signal is meaningful: these probes have essentially no false-positive
    // path, since a resource can only be served if the extension is actually installed.
    detected,
    // A negative we cannot stand behind: nothing was probed, or a probe was cut short
    // before it could answer. Keeping this distinct from a real negative is what makes
    // both the origin skip and an explicit `maxProbeMs` safe.
    inconclusive: !detected && (originMismatch || scoring.some((s) => s.truncated)),
    confidence: totalWeight > 0 ? firedWeight / totalWeight : 0,
    signals,
    durationMs: performance.now() - start,
  };
}

/** Run `fn` over `items` at most `limit` at a time, preserving input order in the result. */
async function mapLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      results[i] = await fn(items[i]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
}

const byInterest = (a: ExtensionResult, b: ExtensionResult) =>
  Number(b.detected) - Number(a.detected) || b.confidence - a.confidence;

const reportOf = (
  results: ExtensionResult[],
  startedAt: string,
  start: number,
  extra: Partial<DetectionReport> = {},
): DetectionReport => ({
  startedAt,
  durationMs: performance.now() - start,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  results: results.slice().sort(byInterest),
  ...extra,
});

/**
 * Run only the tier that answers immediately.
 *
 * This is the fingerprint a page can actually establish before a visitor has finished
 * looking at it: every resource probe, no waiting. Prefer it for anything real-time.
 */
export async function detectFast(
  defs: ExtensionDefinition[],
  { concurrency = 16, maxProbeMs = null }: DetectAllOptions = {},
): Promise<DetectionReport> {
  const start = performance.now();
  const startedAt = new Date().toISOString();
  const { fast } = partitionBySpeed(defs);
  const results = await mapLimit(fast, concurrency, (d) => detectExtension(d, { maxProbeMs }));
  return reportOf(results, startedAt, start, { tier: 'fast' });
}

/**
 * Run everything, reporting the fast tier as soon as it is ready.
 *
 * The two tiers are dispatched together rather than one after the other: a behavioural
 * probe is a timer, not work, so the ~21 slow definitions occupy no scheduler capacity
 * while they wait and cost nothing to start early. They get their own worker pool for the
 * same reason — capping them at `concurrency` would make them queue behind each other and
 * serialise timeouts that could have overlapped.
 *
 * `onFastTier` fires at ~1s with a complete, usable report; the returned promise settles
 * when the slow tier lands too.
 */
export async function detectAll(
  defs: ExtensionDefinition[],
  { concurrency = 16, maxProbeMs = null, onFastTier, onResult }: DetectAllOptions = {},
): Promise<DetectionReport> {
  const start = performance.now();
  const startedAt = new Date().toISOString();
  const { fast, slow } = partitionBySpeed(defs);

  const run = (def: ExtensionDefinition) =>
    detectExtension(def, { maxProbeMs }).then((r) => {
      onResult?.(r);
      return r;
    });

  // Started first and never awaited here, so the timers are already running while the
  // fast tier does its work.
  const slowPromise = mapLimit(slow, Math.max(slow.length, 1), run);
  const fastResults = await mapLimit(fast, concurrency, run);

  const fastMs = performance.now() - start;
  onFastTier?.(reportOf(fastResults, startedAt, start, { tier: 'fast' }));

  const slowResults = await slowPromise;
  return reportOf([...fastResults, ...slowResults], startedAt, start, {
    tier: 'full',
    fastTierMs: fastMs,
  });
}
